import { useEffect, useState } from 'react';
import { Loader2, Search, UserCheck } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { notasFiscaisApi, formatarDocumento } from '@/lib/notasFiscaisApi';
import {
  INDICADOR_IE_LABEL,
  UFS,
  type ClienteFiscal,
  type DestinatarioNota,
  type IndicadorIe,
} from '@/types/notasFiscais';

interface DestinatarioFormProps {
  destinatario: DestinatarioNota;
  onChange: (destinatario: DestinatarioNota) => void;
  leadId: string | null;
  onSelecionarCliente: (leadId: string | null) => void;
}

export function destinatarioDeCliente(c: ClienteFiscal): DestinatarioNota {
  const pj = Boolean(c.cnpj && c.cnpj.replace(/\D/g, '').length === 14);
  return {
    tipo_pessoa: pj ? 'PJ' : 'PF',
    nome: (pj ? c.razao_social || c.nome : c.nome) || '',
    cpf: pj ? '' : c.cpf || '',
    cnpj: pj ? c.cnpj || '' : '',
    indicador_ie: pj ? (c.indicador_ie ?? (c.inscricao_estadual ? 1 : 9)) : 9,
    inscricao_estadual: c.inscricao_estadual || '',
    email: c.email || '',
    telefone: c.telefone || '',
    cep: c.cep || '',
    logradouro: c.endereco || '',
    numero: c.numero || '',
    complemento: c.complemento || '',
    bairro: c.bairro || '',
    municipio: c.cidade || '',
    codigo_municipio: c.codigo_municipio || '',
    uf: (c.estado || '').toUpperCase(),
  };
}

function Obrigatorio() {
  return <span className="ml-0.5 text-destructive" aria-hidden="true">*</span>;
}

export function DestinatarioForm({ destinatario: d, onChange, leadId, onSelecionarCliente }: DestinatarioFormProps) {
  const [busca, setBusca] = useState('');
  const [resultados, setResultados] = useState<ClienteFiscal[]>([]);
  const [buscando, setBuscando] = useState(false);
  const [buscandoCep, setBuscandoCep] = useState(false);

  const set = <K extends keyof DestinatarioNota>(campo: K, valor: DestinatarioNota[K]) =>
    onChange({ ...d, [campo]: valor });

  useEffect(() => {
    const termo = busca.trim();
    if (termo.length < 2) {
      setResultados([]);
      return;
    }
    const timer = setTimeout(async () => {
      setBuscando(true);
      try {
        setResultados(await notasFiscaisApi.buscarClientes(termo));
      } catch {
        setResultados([]);
      } finally {
        setBuscando(false);
      }
    }, 350);
    return () => clearTimeout(timer);
  }, [busca]);

  const selecionar = (cliente: ClienteFiscal) => {
    onChange(destinatarioDeCliente(cliente));
    onSelecionarCliente(cliente.id);
    setBusca('');
    setResultados([]);
  };

  const buscarCep = async () => {
    const cep = d.cep.replace(/\D/g, '');
    if (cep.length !== 8) return;
    setBuscandoCep(true);
    try {
      const res = await fetch(`https://viacep.com.br/ws/${cep}/json/`);
      const data = await res.json();
      if (!data.erro) {
        onChange({
          ...d,
          logradouro: data.logradouro || d.logradouro,
          bairro: data.bairro || d.bairro,
          municipio: data.localidade || d.municipio,
          uf: data.uf || d.uf,
          codigo_municipio: data.ibge || d.codigo_municipio,
        });
      }
    } finally {
      setBuscandoCep(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <Label>Buscar cliente cadastrado</Label>
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            className="pl-9"
            placeholder="Nome, razão social, CPF ou CNPJ"
            value={busca}
            onChange={(e) => setBusca(e.target.value)}
          />
          {buscando && <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 animate-spin" />}
        </div>
        {resultados.length > 0 && (
          <ul className="max-h-48 overflow-auto rounded-md border divide-y">
            {resultados.map((c) => (
              <li key={c.id}>
                <button
                  type="button"
                  className="w-full text-left px-3 py-2 text-sm hover:bg-accent"
                  onClick={() => selecionar(c)}
                >
                  <span className="font-medium">{c.razao_social || c.nome}</span>
                  <span className="text-muted-foreground ml-2">
                    {formatarDocumento(c.cnpj || c.cpf)} {c.cidade ? `- ${c.cidade}/${c.estado || ''}` : ''}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        )}
        {leadId && (
          <p className="text-xs text-muted-foreground flex items-center gap-1">
            <UserCheck className="h-3 w-3" /> Vinculada a um cliente do CRM.
            <button type="button" className="underline ml-1" onClick={() => onSelecionarCliente(null)}>
              Desvincular
            </button>
          </p>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-6 gap-3">
        <div className="md:col-span-2 space-y-1">
          <Label>Tipo</Label>
          <Select
            value={d.tipo_pessoa}
            onValueChange={(v) =>
              onChange({ ...d, tipo_pessoa: v as DestinatarioNota['tipo_pessoa'], indicador_ie: v === 'PF' ? 9 : d.indicador_ie })
            }
          >
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="PF">Pessoa física</SelectItem>
              <SelectItem value="PJ">Pessoa jurídica</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="md:col-span-4 space-y-1">
          <Label>{d.tipo_pessoa === 'PJ' ? 'Razão social' : 'Nome completo'}<Obrigatorio /></Label>
          <Input value={d.nome} onChange={(e) => set('nome', e.target.value)} />
        </div>

        <div className="md:col-span-2 space-y-1">
          <Label>{d.tipo_pessoa === 'PJ' ? 'CNPJ' : 'CPF'}<Obrigatorio /></Label>
          <Input
            value={d.tipo_pessoa === 'PJ' ? d.cnpj || '' : d.cpf || ''}
            onChange={(e) => set(d.tipo_pessoa === 'PJ' ? 'cnpj' : 'cpf', e.target.value)}
          />
        </div>
        {d.tipo_pessoa === 'PJ' && (
          <>
            <div className="md:col-span-2 space-y-1">
              <Label>Situação de ICMS</Label>
              <Select value={String(d.indicador_ie)} onValueChange={(v) => set('indicador_ie', Number(v) as IndicadorIe)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {([1, 2, 9] as IndicadorIe[]).map((i) => (
                    <SelectItem key={i} value={String(i)}>{INDICADOR_IE_LABEL[i]}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="md:col-span-2 space-y-1">
              <Label>Inscrição estadual{d.indicador_ie === 1 && <Obrigatorio />}</Label>
              <Input
                value={d.inscricao_estadual || ''}
                disabled={d.indicador_ie !== 1}
                onChange={(e) => set('inscricao_estadual', e.target.value)}
              />
            </div>
          </>
        )}

        <div className="md:col-span-3 space-y-1">
          <Label>E-mail (recebe a DANFE e o XML)</Label>
          <Input type="email" value={d.email || ''} onChange={(e) => set('email', e.target.value)} />
        </div>
        <div className="md:col-span-3 space-y-1">
          <Label>Telefone</Label>
          <Input value={d.telefone || ''} onChange={(e) => set('telefone', e.target.value)} />
        </div>

        <div className="md:col-span-2 space-y-1">
          <Label>CEP<Obrigatorio /></Label>
          <div className="flex gap-2">
            <Input value={d.cep} onChange={(e) => set('cep', e.target.value)} onBlur={buscarCep} />
            <Button type="button" variant="outline" size="icon" onClick={buscarCep} disabled={buscandoCep}>
              {buscandoCep ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
            </Button>
          </div>
        </div>
        <div className="md:col-span-3 space-y-1">
          <Label>Logradouro<Obrigatorio /></Label>
          <Input value={d.logradouro} onChange={(e) => set('logradouro', e.target.value)} />
        </div>
        <div className="md:col-span-1 space-y-1">
          <Label>Número<Obrigatorio /></Label>
          <Input value={d.numero} onChange={(e) => set('numero', e.target.value)} />
        </div>
        <div className="md:col-span-2 space-y-1">
          <Label>Complemento</Label>
          <Input value={d.complemento || ''} onChange={(e) => set('complemento', e.target.value)} />
        </div>
        <div className="md:col-span-2 space-y-1">
          <Label>Bairro<Obrigatorio /></Label>
          <Input value={d.bairro} onChange={(e) => set('bairro', e.target.value)} />
        </div>
        <div className="md:col-span-1 space-y-1">
          <Label>UF<Obrigatorio /></Label>
          <Select value={d.uf || undefined} onValueChange={(v) => set('uf', v)}>
            <SelectTrigger><SelectValue placeholder="UF" /></SelectTrigger>
            <SelectContent>
              {UFS.map((uf) => <SelectItem key={uf} value={uf}>{uf}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="md:col-span-1 space-y-1">
          <Label>Cód. IBGE</Label>
          <Input value={d.codigo_municipio || ''} onChange={(e) => set('codigo_municipio', e.target.value)} />
        </div>
        <div className="md:col-span-6 space-y-1">
          <Label>Município<Obrigatorio /></Label>
          <Input value={d.municipio} onChange={(e) => set('municipio', e.target.value)} />
        </div>
      </div>
      <p className="text-xs text-muted-foreground"><span className="text-destructive">*</span> Campos obrigatórios</p>
    </div>
  );
}
